import LandingTemplate from "@/components/LandingTemplate";
import { RetirementCalculator } from "@/components/Calculators";

export default function Retirement() {
  return (
    <LandingTemplate
      audience="individual"
      seo={{
        path: "/retirement-planning",
        title: "Plan for Retirement",
        description: "Estimate the retirement corpus you may need and the monthly SIP to close the gap — accounting for inflation and withdrawals. Illustrative planning by Rao Investment Solutions.",
      }}
      breadcrumbs={[{ name: "Home", to: "/" }, { name: "Resources" }, { name: "Plan for Retirement" }]}
      hero={{
        label: "Resource",
        title: "Retire on your terms — not on hope.",
        intro: "Retirement is the longest expense of your life. We help you estimate what you'll need and build a realistic path to get there.",
        cta: "Plan my retirement",
      }}
      painPoints={[
        "I have no idea how much I'll actually need to retire.",
        "Will my savings run out before I do?",
        "Inflation scares me — today's expenses won't be tomorrow's.",
        "I started late; can I still catch up?",
        "My money is scattered and I can't see the full picture.",
      ]}
      whyMatters={{
        title: "The number is bigger than most people think.",
        paras: [
          "Rising life expectancy means your corpus may need to fund 25–30 years of expenses after you stop earning.",
          "Inflation compounds against you: a comfortable expense today can more than triple over a few decades.",
          "The earlier you plan, the smaller the monthly effort. Starting late is possible — it just asks more of each rupee.",
        ],
      }}
      approach={{
        title: "A clear retirement roadmap",
        intro: "We turn an intimidating question into a monthly number.",
        stages: [
          { title: "Project needs", body: "Inflate today's expenses to your retirement age to size the real requirement." },
          { title: "Size the corpus", body: "Estimate the corpus needed to fund your withdrawal years after inflation." },
          { title: "Assess the gap", body: "Compare against your existing investments' likely future value." },
          { title: "Fund the gap", body: "Back out the monthly SIP needed to close any shortfall — and review it yearly." },
        ],
      }}
      solutions={[
        { name: "Retirement SIPs", to: "/products/mf-pms-aif", desc: "Long-horizon equity and hybrid SIPs to build the corpus over time." },
        { name: "PMS & AIF", to: "/products/mf-pms-aif", desc: "For eligible investors seeking customisation as the corpus grows (SEBI minimums apply)." },
        { name: "Income & annuity mix", to: "/products/fixed-income-insurance", desc: "Fixed income and structured withdrawals to draw a steady post-retirement income." },
      ]}
      calculator={<RetirementCalculator />}
      calculatorTitle="Estimate your retirement corpus & required SIP"
      howWeHelp={[
        { title: "Retirement review", body: "We assess your current savings and goals." },
        { title: "Gap analysis", body: "A clear picture of the shortfall to close." },
        { title: "Funding plan", body: "A monthly SIP and allocation to reach the target." },
        { title: "Annual tracking", body: "We revisit assumptions and adjust as life changes." },
      ]}
      whoFor={[
        "Professionals in their 30s and 40s planning ahead",
        "Business owners without a formal pension",
        "Those nearing retirement wanting a withdrawal plan",
        "Anyone unsure if they're saving enough",
      ]}
      faq={[
        { q: "How is the required corpus calculated?", a: "We inflate your current monthly expense to retirement age, then estimate the corpus needed to fund your withdrawal years using a real-return (inflation-adjusted) assumption. It's an illustrative estimate, not a guarantee." },
        { q: "What if I've started late?", a: "You can still make meaningful progress. The plan may need a higher monthly contribution or an adjusted retirement age — we'll show you the trade-offs clearly." },
        { q: "Do the numbers account for inflation?", a: "Yes. Both the future expense and the withdrawal-phase returns are considered in real terms, using assumptions you can adjust." },
      ]}
      finalCta={{ title: "Know your number. Then build toward it.", intro: "Book a retirement planning session and leave with a corpus target and a monthly plan." }}
    />
  );
}
